import { useEffect, useRef } from 'react';
import * as THREE from 'three';

/**
 * DottedSurface — grade de partículas animada com ondas senoidais (Three.js).
 * Recebe `theme` ('dark' | 'light') para cor dos pontos.
 */
export function DottedSurface({ theme = 'dark', style, ...props }) {
    const containerRef = useRef(null);
    // Ref separado para animationId, sempre atual dentro do loop
    const animIdRef = useRef(null);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const SEPARATION = 150;
        const AMOUNTX = 40;
        const AMOUNTY = 60;

        const W = window.innerWidth;
        const H = window.innerHeight;

        // ── Cena ──
        const scene = new THREE.Scene();

        const camera = new THREE.PerspectiveCamera(60, W / H, 1, 10000);
        camera.position.set(0, 355, 1220);

        const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
        renderer.setPixelRatio(window.devicePixelRatio);
        renderer.setSize(W, H);
        renderer.setClearColor(0x000000, 0);
        el.appendChild(renderer.domElement);

        // ── Geometria ──
        const positions = [];
        const colors = [];

        for (let ix = 0; ix < AMOUNTX; ix++) {
            for (let iy = 0; iy < AMOUNTY; iy++) {
                positions.push(
                    ix * SEPARATION - (AMOUNTX * SEPARATION) / 2,
                    0,
                    iy * SEPARATION - (AMOUNTY * SEPARATION) / 2,
                );
                if (theme === 'dark') {
                    colors.push(0.78, 0.78, 0.78);
                } else {
                    colors.push(0.10, 0.10, 0.10);
                }
            }
        }

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

        const material = new THREE.PointsMaterial({
            size: 8,
            vertexColors: true,
            transparent: true,
            opacity: 0.75,
            sizeAttenuation: true,
        });

        const points = new THREE.Points(geometry, material);
        scene.add(points);

        // ── Loop ──
        let count = 0;

        const animate = () => {
            animIdRef.current = requestAnimationFrame(animate);

            const posAttr = geometry.attributes.position;
            const arr = posAttr.array;
            let i = 0;
            for (let ix = 0; ix < AMOUNTX; ix++) {
                for (let iy = 0; iy < AMOUNTY; iy++) {
                    arr[i * 3 + 1] =
                        Math.sin((ix + count) * 0.3) * 50 +
                        Math.sin((iy + count) * 0.5) * 50;
                    i++;
                }
            }
            posAttr.needsUpdate = true;
            renderer.render(scene, camera);
            count += 0.032;
        };

        animate();

        // ── Resize ──
        const onResize = () => {
            const w = window.innerWidth;
            const h = window.innerHeight;
            camera.aspect = w / h;
            camera.updateProjectionMatrix();
            renderer.setSize(w, h);
        };
        window.addEventListener('resize', onResize);

        // ── Cleanup ──
        return () => {
            window.removeEventListener('resize', onResize);
            // Cancela o frame atual (ref sempre atual)
            if (animIdRef.current !== null) {
                cancelAnimationFrame(animIdRef.current);
                animIdRef.current = null;
            }
            geometry.dispose();
            material.dispose();
            renderer.dispose();
            if (el.contains(renderer.domElement)) {
                el.removeChild(renderer.domElement);
            }
        };
    }, [theme]);

    return (
        <div
            ref={containerRef}
            style={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                zIndex: 0,
                ...style,
            }}
            {...props}
        />
    );
}
